
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useDemo } from "../../context/DemoContext";
import { ViewState } from "../../types";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileNav } from "./MobileNav";
import { Footer } from "./Footer";

interface AppShellProps {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  children: React.ReactNode;
  onConnectWallet?: () => void;
  isSidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}

export const AppShell: React.FC<AppShellProps> = ({ currentView, onNavigate, children, onConnectWallet, isSidebarCollapsed, onToggleSidebar }) => {
  const { user } = useAuth();
  const { isDemoMode, activeRole } = useDemo();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const currentRole = isDemoMode ? activeRole : user?.role || 'client';

  const handleNavigate = (view: ViewState) => {
    onNavigate(view);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] dark:bg-[#0A0B0F] flex transition-colors duration-300">
      <Sidebar 
        currentView={currentView} 
        onNavigate={handleNavigate} 
        currentRole={currentRole}
        isDemoMode={isDemoMode}
        activeRole={activeRole}
        isSidebarCollapsed={isSidebarCollapsed}
        onToggleSidebar={onToggleSidebar}
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
      />

      <div className={`flex-1 flex flex-col min-h-screen relative w-full transition-all duration-300 ${isSidebarCollapsed ? 'md:pl-20' : 'md:pl-72'}`}>
        <Header 
          currentView={currentView}
          onNavigate={onNavigate}
          onConnectWallet={onConnectWallet}
          onToggleSidebar={onToggleSidebar}
        />
        <MobileNav 
            currentView={currentView}
            onNavigate={handleNavigate}
            onConnectWallet={onConnectWallet}
            onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
        />

        <main className="flex-1 flex flex-col">
            <div className="flex-1 p-4 md:p-8 overflow-y-auto bg-[#F8F9FA] dark:bg-[#0A0B0F] text-[#0A0B0F] dark:text-gray-300 transition-colors duration-300 pb-28 md:pb-8">
                <div className="max-w-7xl mx-auto">
                    {children}
                </div>
            </div>
            <Footer />
        </main>
      </div>
    </div>
  );
};
